import { NotFoundPage } from "@components/NotFoundPage";
import { db } from "@db";
import type { GnomuxSequence } from "@db/content";
import type { GnomuxFile } from "@db/objects";
import { getObject } from "@db/tree";
import { useTrackRecentView } from "@db/useTrackRecentView";
import { useDelayedLoading } from "@hooks/useDelayedLoading";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useLiveQuery } from "dexie-react-hooks";
import { Loader2Icon } from "lucide-react";
import { useEffect, useRef } from "react";
import {
  ViewerFileContext,
  ViewerSequencesContext,
} from "./-viewerFileContext";

export const Route = createFileRoute("/_data/view/$fileId")({
  component: ViewLayout,
});

function ViewLayout() {
  const { fileId } = Route.useParams();
  useTrackRecentView(fileId);

  const result = useLiveQuery(async () => {
    const file = (await getObject(fileId)) as GnomuxFile | undefined;
    if (!file) return undefined;

    if (file.type === "GnomuxSequenceCollection") {
      const ids = file.content as string[];
      const sequences = await db.sequences.bulkGet(ids);
      return {
        file,
        sequences: sequences.filter(
          (s): s is GnomuxSequence => s !== undefined,
        ),
      };
    }

    return { file, sequences: undefined };
  }, [fileId]);

  const file = result?.file;
  const sequences = result?.sequences;

  const navigate = useNavigate();
  const prevFileRef = useRef<GnomuxFile | null>(null);

  const isDeleted =
    file?.deletedAt !== undefined || (!file && prevFileRef.current !== null);

  useEffect(() => {
    if (isDeleted) {
      navigate({ to: "/", replace: true });
    }
  }, [isDeleted, navigate]);

  if (file && !file.deletedAt) {
    prevFileRef.current = file;
  }

  const displayFile = file?.deletedAt ? null : (file ?? prevFileRef.current);
  const showLoading = useDelayedLoading(!displayFile);

  if (!displayFile) {
    if (showLoading) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    return (
      <NotFoundPage
        heading="File not found"
        description="This file doesn't exist or has been deleted."
      />
    );
  }

  return (
    <ViewerFileContext.Provider value={displayFile}>
      <ViewerSequencesContext.Provider value={sequences}>
        <Outlet />
      </ViewerSequencesContext.Provider>
    </ViewerFileContext.Provider>
  );
}
